import { ReactNode, createContext, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCurrentSubscription } from '@api/hooks/useSubscription';
import Button from '@components/common/Button';
import { formatDate } from '@utils/formatters';

const ReadOnlyContext = createContext<boolean>(false);
export const useReadOnly = () => useContext(ReadOnlyContext);

const EXEMPT_PATH_PREFIXES = [
  '/settings/billing',
  '/settings/account',
  '/onboarding',
];

const BILLING_SETTINGS_PATH = '/admin/settings?tab=billing';

export function BillingGate({ children }: { children: ReactNode }) {
  const sub = useCurrentSubscription();
  const location = useLocation();
  const navigate = useNavigate();

  const exempt = EXEMPT_PATH_PREFIXES.some((p) =>
    location.pathname.startsWith(p),
  ) || (
    location.pathname === '/admin/settings'
    && new URLSearchParams(location.search).get('tab') === 'billing'
  );

  // While loading: don't gate (avoid flashing a banner that goes away)
  if (sub.isLoading || !sub.data) {
    return <>{children}</>;
  }

  if (exempt) {
    return <>{children}</>;
  }

  const state = sub.data.gating_state ?? 'unrestricted';

  if (state === 'blocked') {
    const trialEnded = Boolean(
      sub.data.trial_ends_at &&
      new Date(sub.data.trial_ends_at).getTime() <= Date.now(),
    );
    const trialBlocked = sub.data.status === 'trialing' || (
      trialEnded &&
      sub.data.billing_mode === 'card' &&
      (
        ['past_due', 'unpaid', 'incomplete'].includes(sub.data.status) ||
        !sub.data.payment_method_on_file_at
      )
    );
    return (
      <div className="max-w-md mx-auto mt-16 p-6 text-center">
        <h1 className="text-xl font-medium text-red-700">
          {trialBlocked ? 'Trial ended' : 'Subscription inactive'}
        </h1>
        <p className="text-sm text-content-secondary mt-2">
          Update billing to continue using EZ AP.
        </p>
        <Button
          onClick={() => navigate(BILLING_SETTINGS_PATH)}
          className="mt-4"
        >
          Go to Billing
        </Button>
      </div>
    );
  }

  const banner = (() => {
    if (state === 'banner_past_due') {
      return (
        <div className="bg-red-50 border-b border-red-200 px-4 py-2 text-sm text-red-800 text-center">
          <strong>Payment past due.</strong> Add a payment method to continue.
          <button
            type="button"
            onClick={() => navigate(BILLING_SETTINGS_PATH)}
            className="ml-2 underline"
          >
            Add card
          </button>
        </div>
      );
    }
    if (state === 'banner_trial_ending') {
      return (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-sm text-amber-900 text-center">
          {sub.data.trial_ends_at &&
            `Trial ends ${formatDate(sub.data.trial_ends_at)}.`}
          <button
            type="button"
            onClick={() => navigate(BILLING_SETTINGS_PATH)}
            className="ml-2 underline"
          >
            Manage billing
          </button>
        </div>
      );
    }
    if (state === 'readonly') {
      return (
        <div className="bg-red-50 border-b border-red-200 px-4 py-2 text-sm text-red-800 text-center">
          <strong>Payment past due.</strong> Read-only mode active until
          payment is updated.
          <button
            type="button"
            onClick={() => navigate(BILLING_SETTINGS_PATH)}
            className="ml-2 underline"
          >
            Update payment
          </button>
        </div>
      );
    }
    return null;
  })();

  return (
    <ReadOnlyContext.Provider value={state === 'readonly'}>
      {banner}
      {children}
    </ReadOnlyContext.Provider>
  );
}
